'use es6';

import I18n from 'I18n';
import { List, Map as ImmutableMap, fromJS } from 'immutable';
import { compareLabelFactory } from 'reporting-data/compare';
import { AREA, BAR, COLUMN, COMBINATION, DATA_WELL, DONUT, LINE, PIE, TABLE } from 'reporting-data/constants/chartTypes';
import { FUNNEL as FUNNEL_CONFIG_TYPE, PIPELINE, SEARCH, TIME_SERIES } from 'reporting-data/constants/configTypes';
import { ATTRIBUTION_TOUCH_POINTS, COMPANIES, CONTACTS, CONTACT_CREATE_ATTRIBUTION, CRM_OBJECT, DEALS, LINE_ITEMS, UNIFIED_EVENTS } from 'reporting-data/constants/dataTypes';
import { SUM } from 'reporting-data/constants/metricTypes';
import { ACCUMULATE, ZERO_DIMENSIONAL_METRIC_FUNNEL } from 'reporting-data/constants/processorTypes';
import { CUSTOM_SERIES_DATASET_PREFIX } from 'reporting-data/customSeries/resolveV2CustomSeries';
import { ZERO_DIMENSIONAL_METRIC_FUNNEL_DIMENSION, ZERO_DIMENSIONAL_METRIC_FUNNEL_METRIC } from 'reporting-data/process/zeroDimensionalMetricFunnel';
import { LINKS_FIELD } from 'reporting-data/v2/dataset/constants';
import { fromMetricKey, toMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { Dataset, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import { RANGE_TYPES } from 'reporting-data/constants/dateRangeTypes';
import { BATTLESHIP_TRANSPARENT, SLINKY } from 'reporting-visualizations/constants/colors';
import { FUNNEL } from 'reporting-visualizations/constants/visualizationTypes';
import { DatawellVisualization } from 'reporting-visualizations/visualizations/datawell/datawellRecords';
import { cumulativeConversionProperty, stepwiseConversionProperty } from 'reporting-visualizations/visualizations/funnel/funnelConversionConstants';
import { FunnelVisualization } from 'reporting-visualizations/visualizations/funnel/funnelRecords';
import { PivotVisualization } from 'reporting-visualizations/visualizations/pivot/pivotRecords';
import { SlicedVisualization } from 'reporting-visualizations/visualizations/sliced/slicedRecords';
import { getCustomRendererKey } from 'reporting-visualizations/visualizations/table/components/customCellRenderers';
import { TableVisualization } from 'reporting-visualizations/visualizations/table/tableRecords';
import { AxisTypes, MarkTypes, XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { getTotal, transposeChart, buildXYInsightLayerOverrides } from 'reporting-visualizations/visualizations/xy/xyUtils';
import { applySlicedDisplayParams, applyXYDisplayParams } from './displayParams/applyDisplayParams';
import { DEFAULT_FISCAL_YEAR_MONTH_NAME } from 'reporting-data/constants/dates';
import { getUseFiscalYear } from 'reporting-data/lib/fiscalYearUtils';
import { findDatasetInsights } from 'reporting-data/datasetInsights';
import { getCompareDataset, getPrimaryDataset, getDatasetInsightsMetricName, getDatasetInsightsRangeName } from 'reporting-data/v2/dataset/datasetGetters';
import { getOnTableCellLinkClickCallback } from '../../lib/usageTrackerUtils';
import { ONSORT_PATH } from '../../components/PlatformReport/useSortableReport';
const searchDimensionMap = ImmutableMap({
  [ATTRIBUTION_TOUCH_POINTS]: 'objectId',
  [COMPANIES]: 'company-id',
  [CONTACTS]: 'vid',
  [CONTACT_CREATE_ATTRIBUTION]: 'objectId',
  [DEALS]: 'dealId',
  [LINE_ITEMS]: 'objectId'
});
const SORT_MAPPING = {
  ASC: 'ascending',
  DESC: 'descending'
};
export const getColumnOptions = (report, metric) => report.getIn(['displayParams', 'columns', fromMetricKey(metric).property], ImmutableMap()).merge(report.getIn(['displayParams', 'columns', toMetricKey(fromMetricKey(metric))], ImmutableMap())).merge(report.getIn(['displayParams', 'columns', metric], ImmutableMap()));
export const inferTable = (report, dimensions = List(), metrics, dataMap, isMobile, mobileReportingOptions = {}) => {
  const paginationSize = dataMap.getIn(['primary', 'data', 'paginationSize']);
  const reportLimit = report.getIn(['config', 'limit']);
  const pages = paginationSize && reportLimit && Math.ceil(paginationSize / reportLimit);
  const isSearch = report.getIn(['config', 'configType']) === SEARCH;
  if ([FUNNEL].includes(report.getIn(['config', 'configType'])) && [CONTACTS, DEALS].includes(report.getIn(['config', 'dataType'])) && metrics.count() === 1) {
    metrics = metrics.concat([stepwiseConversionProperty, cumulativeConversionProperty]);
  }
  const toggleSeriesHandler = report.getIn(['displayParams', 'toggleSeries']);
  const toggleSeriesParamsObj = {};
  if (toggleSeriesHandler) {
    const toggleAllSeriesHandler = report.getIn(['displayParams', 'toggleAllSeries']);
    const seriesToggleDisabled = report.getIn(['displayParams', 'seriesToggleDisabled']);
    const checkedSeries = report.getIn(['displayParams', 'checked']);
    const colors = report.getIn(['displayParams', 'colors', 'custom']) || ImmutableMap();
    toggleSeriesParamsObj.toggleSeriesParams = {
      toggleSeriesHandler,
      toggleAllSeriesHandler,
      seriesToggleDisabled,
      checkedSeries,
      colors,
      dimension: dimensions.first()
    };
  }
  const columns = dimensions.map(dimension => {
    return {
      dataset: 'primary',
      field: dimension
    };
  }).concat(metrics.map(metric => {
    const {
      property,
      type
    } = fromMetricKey(metric);
    const field = isSearch ? property : metric;
    const column = getColumnOptions(report, metric);
    const tooltipI18nKey = column.get('tooltipI18nKey');
    const direction = (report.getIn(['config', 'sort']) || List()).find(sortObj => sortObj.get('property') === property && (!sortObj.get('metricType') || sortObj.get('metricType') === type), null, ImmutableMap()).get('order');
    const onSort = column.get('onSort') || report.getIn(ONSORT_PATH);
    return Object.assign({
      dataset: 'primary',
      field,
      direction: SORT_MAPPING[direction] || direction,
      onSort,
      tooltipI18nKey,
      useRawDataLabel: field === 'hs_object_id' && report.getIn(['config', 'dataType']) !== CRM_OBJECT
    }, column.get('showAsBarChart', false) ? {
      showAsBarChart: true,
      showAsBarChartOptions: column.get('showAsBarChartOptions', ImmutableMap())
    } : {});
  }))
  // Columns that need to be hidden
  .filter(column => !getColumnOptions(report, column.field).get('hideColumn')).map(column => {
    const {
      field
    } = column;
    const property = fromMetricKey(field).property;
    const onClick = getColumnOptions(report, field).get('onClick');
    column.onClick = !onClick ? null : clickEvent => {
      const valueLabels = clickEvent.valueLabels || clickEvent.column && clickEvent.column.get('valueLabels') || clickEvent.cell && clickEvent.cell.get('valueLabels');
      const dimension = report.getIn(['config', 'dimensions', 0]);
      const row = clickEvent.row || clickEvent.cell.row;
      const seriesKey = row.get(dimension);
      const seriesLabel = getReferenceLabel(clickEvent.dataset, dimension, seriesKey);
      const newArgs = {
        metricKey: property,
        metricLabel: valueLabels.get(0),
        seriesKey,
        seriesLabel,
        category: {
          key: seriesKey,
          label: seriesLabel
        },
        report
      };
      onClick(Object.assign({}, clickEvent, newArgs));
    };
    const columnOptions = getColumnOptions(report, field);
    column.renderCell = columnOptions.get('render');
    column.style = fromJS({
      format: columnOptions.get('format')
    });
    if (!column.renderCell) {
      column.customRendererKey = getCustomRendererKey({
        report,
        column,
        dataMap
      });
    }
    return column;
  }).update(0, col => {
    if (report.getIn(['displayParams', 'onRowClick']) && !col.onClick) {
      const onClickFunc = report.getIn(['displayParams', 'onRowClick']);
      col.onClick = ({
        value,
        valueLabels
      }) => {
        onClickFunc({
          key: value,
          label: valueLabels.first()
        });
      };
    }
    return col;
  });
  if (!isSearch && dimensions.size > 1) {
    return PivotVisualization({
      data: dataMap,
      columns,
      pages,
      pivot: {
        dataset: 'primary',
        field: dimensions.get(1),
        groupby: List([dimensions.get(0)]),
        value: metrics.get(0)
      },
      showTotalsColumn: report.getIn(['displayParams', 'showTotalsColumn']),
      showTotals: report.getIn(['displayParams', 'showTotals'])
    });
  }
  const {
    enableDeepLinking
  } = mobileReportingOptions;
  if (isMobile && !enableDeepLinking) {
    // Disable links for mobile webviews
    dataMap = dataMap.update(datasets => datasets.map(dataset => dataset.updateIn(['data', 'data'], rows => rows.map(row => row.delete(LINKS_FIELD)))));
  }
  const onTableCellLinkClick = getOnTableCellLinkClickCallback(report);
  return TableVisualization(Object.assign({
    data: {
      primary: dataMap.getIn(['primary', 'data']).update(data => {
        const currentDataSize = data.get('data').size;
        const total = data.get('paginationSize');
        const limit = report.getIn(['config', 'limit']);
        if (currentDataSize < total || !limit) {
          return data;
        }
        const offset = report.getIn(['config', 'offset'], 0);
        return data.update('data', datum => datum.skip(offset).take(limit));
      }),
      summary: dataMap.getIn(['summary', 'data'])
    },
    columns,
    pages,
    footer: 'summary',
    showTotals: report.getIn(['displayParams', 'showTotals']),
    onTableCellLinkClick,
    style: {
      isMobile
    }
  }, toggleSeriesParamsObj));
};
const inferXYChartLayers = (datasetKey, dimensions, metrics, overrides) => {
  if (dimensions.count() === 2) {
    return List.of(Object.assign({
      dataset: datasetKey,
      x: {
        type: AxisTypes.category,
        field: dimensions.first(),
        blend: 'bottom'
      },
      y: {
        type: AxisTypes.linear,
        field: metrics.first(),
        blend: 'left'
      },
      color: {
        field: dimensions.get(1)
      }
    }, overrides || {}));
  }
  return metrics.map(metric => {
    return Object.assign({
      dataset: datasetKey,
      x: {
        type: AxisTypes.category,
        field: dimensions.first(),
        blend: 'bottom'
      },
      y: {
        type: AxisTypes.linear,
        field: metric,
        blend: 'left'
      }
    }, overrides || {});
  });
};
const inferXYInsightLayers = (report, datasetKey, dataset, dimensions) => {
  var _report$getIn;
  const metricName = getDatasetInsightsMetricName(dataset);
  const rangeName = getDatasetInsightsRangeName(dataset);
  const hideDataLabels = (_report$getIn = report.getIn(['displayParams', 'hideDataLabels'])) !== null && _report$getIn !== void 0 ? _report$getIn : true;
  const insightLayerOverrides = buildXYInsightLayerOverrides(dataset, datasetKey, {
    showDataLabels: !hideDataLabels
  });
  let insightLayers = inferXYChartLayers(datasetKey, dimensions, List([metricName]), insightLayerOverrides.primary);
  if (insightLayerOverrides.range) {
    insightLayers = insightLayers.concat(inferXYChartLayers(datasetKey, dimensions, List([rangeName]), insightLayerOverrides.range));
  }
  return insightLayers;
};
const inferXYComparisonLayers = (datasetKey, dimensions, metrics, chartType) => {
  const color = metrics.count() === 1 ? {
    color: {
      default: BATTLESHIP_TRANSPARENT
    }
  } : {};
  return metrics.map(metric => Object.assign({
    dataset: datasetKey,
    mark: {
      type: chartType === LINE ? AREA : chartType
    },
    x: {
      type: AxisTypes.category,
      field: dimensions.first(),
      blend: 'bottom'
    },
    y: {
      type: AxisTypes.linear,
      field: metric,
      blend: 'left'
    }
  }, color));
};
const inferXYCombinationLayers = (datasetKey, dimensions, metrics) => {
  return metrics.map(metric => ({
    dataset: datasetKey,
    mark: {
      type: 'line'
    },
    x: {
      type: AxisTypes.category,
      field: dimensions.first(),
      blend: 'bottom'
    },
    y: {
      type: AxisTypes.linear,
      field: metric
    },
    color: {
      default: SLINKY
    }
  }));
};
const inferDataWell = (report, dimensions, metrics, dataMap, compact) => {
  const dataset = dataMap.getIn(['primary', 'data']) || Dataset({});
  const comparisonDataset = dataMap.getIn(['compare', 'data']);
  const direction = report.getIn(['displayParams', 'datawellDirection'], dimensions.count() === 1 ? 'vertical' : 'horizontal');
  const group = dimensions.first();
  const columns = report.getIn(['displayParams', 'columns']) || ImmutableMap();
  const abbreviateNumbers = report.getIn(['displayParams', 'abbreviateNumbers']);
  const hiddenColumns = columns.filter(col => col.get('hideColumn'));
  const fields = metrics.filter(metric => hiddenColumns.get(fromMetricKey(metric).property) === undefined && hiddenColumns.get(metric) === undefined);
  const subtitleColumnMap = columns.map(column => column.get('subtitleColumn'));
  const valueFormattingOptions = columns.reduce((acc, options, column) => {
    if (options.has('tooltipI18nKey')) {
      return acc.push(fromJS({
        column,
        options: {
          tooltipI18nKey: options.get('tooltipI18nKey')
        }
      }));
    }
    return acc;
  }, List());
  return DatawellVisualization({
    data: {
      primary: dataset,
      compare: comparisonDataset
    },
    layer: {
      compact,
      dataset: 'primary',
      compare: comparisonDataset ? 'compare' : undefined,
      fields,
      direction,
      group,
      valueFormattingOptions,
      subtitleColumnMap,
      abbreviateNumbers
    }
  });
};
const inferSliced = (report, dimensions, metrics, dataMap, isMobile, brandKitPalettes, disableTooltips) => {
  const chartType = report.get('chartType');
  const dataset = dataMap.getIn(['primary', 'data']) || Dataset({});
  const isGrouped = !report.getIn(['config', 'dimensions']).isEmpty();
  const dateResolution = report.getIn(['config', 'frequency'], undefined);
  return applySlicedDisplayParams(SlicedVisualization({
    data: {
      primary: dataset
    },
    layer: {
      dataset: 'primary',
      // Zero dimension reports don't have a common metric to render sliced
      // charts by. In these cases, default the metric to Count.

      angle: {
        field: isGrouped ? metrics.first() : I18n.text('property.count.label')
      },
      color: {
        field: dimensions.first()
      },
      style: {
        innerSize: chartType === DONUT ? '35%' : '0%'
      }
    },
    isGrouped,
    style: {
      isMobile,
      dateResolution,
      tooltipDisabled: disableTooltips
    }
  }), report, brandKitPalettes);
};
const updateComparisonDatasetPropertyLabels = (report, metrics, primaryDataset, compareDataset) => {
  let compareProperties = compareDataset.get('properties');
  const primaryProperties = primaryDataset.get('properties');
  const compareLabel = compareLabelFactory(report);
  const comparisonLabel = metricLabel => metrics.count() === 1 ? compareLabel(report.getIn(['config', 'compare'])) : compareLabel(report.getIn(['config', 'compare']), metricLabel);
  metrics.forEach(metric => {
    compareProperties = compareProperties.update(metric, metricProperty => {
      if (metricProperty) {
        return metricProperty.set('label', comparisonLabel(metricProperty.get('label', metric)));
      }
      const primaryMetric = primaryProperties.get(metric);
      return primaryMetric.set('references', ImmutableMap()).set('label', comparisonLabel(primaryMetric.get('label', metric)));
    });
  });
  return compareDataset.set('properties', compareProperties);
};
const timeSeriesIsBeyondInferredDateRange = (data, inferredDateRange, timeSeriesDimension) => {
  if (!timeSeriesDimension) {
    return false;
  }
  const inferredEndDate = inferredDateRange.get('endDate');
  const momentInferredEndDate = inferredEndDate && I18n.moment.portalTz(inferredEndDate);
  const actualDataEndDate = data.last().get(timeSeriesDimension);
  const momentActualDataEndDate = actualDataEndDate && I18n.moment.portalTz(actualDataEndDate);
  return momentInferredEndDate && momentActualDataEndDate && momentInferredEndDate.isBefore(momentActualDataEndDate);
};
const updateComparisonDataset = (report, metrics, primaryDataset, comparisonDataset, fiscalYearMonthName) => {
  const primaryData = primaryDataset.get('data');
  const comparisonData = comparisonDataset.get('data');
  const nullFillLength = primaryData[primaryData.size ? 'size' : 'length'] - comparisonData[comparisonData.size ? 'size' : 'length'];
  const maybeTimeSeriesDimension = report.getIn(['config', 'configType']) === TIME_SERIES && report.getIn(['config', 'dimensions', 0]);
  let maybeNullFilledComparisonData = comparisonData;
  if (nullFillLength >= 0) {
    const nullRows = primaryData.slice(0, nullFillLength).map(dataElement => {
      return dataElement.map((val, key) => {
        return metrics.includes(key) || maybeTimeSeriesDimension === key ? null : val;
      });
    });
    const baseDateRangeValue = report.getIn(['config', 'filters', 'dateRange', 'value'], ImmutableMap({}));
    const useFiscalYear = getUseFiscalYear(report.get('config'));
    const inferedPrimaryDateRange = fromJS(makeDateRangeByType(baseDateRangeValue.toJS(), null, report.getIn('config', 'dataType'), useFiscalYear, fiscalYearMonthName));
    const fillToBack = baseDateRangeValue.get('rangeType') === RANGE_TYPES.IS_AFTER_DATE && timeSeriesIsBeyondInferredDateRange(primaryData, inferedPrimaryDateRange, maybeTimeSeriesDimension);
    maybeNullFilledComparisonData = fillToBack ? maybeNullFilledComparisonData.concat(nullRows) : nullRows.concat(maybeNullFilledComparisonData);
  }
  return updateComparisonDatasetPropertyLabels(report, metrics, primaryDataset, comparisonDataset.set('data', maybeNullFilledComparisonData));
};
export const inferXY = (report, dimensions, metrics, dataMap, isMobile, fiscalYearMonthName = DEFAULT_FISCAL_YEAR_MONTH_NAME, brandKitPalettes, disableZoom) => {
  const primaryDataMapItem = getPrimaryDataset(dataMap);
  const primaryDataset = primaryDataMapItem && primaryDataMapItem.get('data') || Dataset({});
  const comparisonDataMapItem = getCompareDataset(dataMap);
  const comparisonDataset = comparisonDataMapItem && comparisonDataMapItem.get('data');
  const insightDatasets = findDatasetInsights(dataMap).map(dataset => dataset.get('data'));
  const hasInsightDatasets = insightDatasets && insightDatasets.count();
  const updatedComparisonDataset = comparisonDataset ? updateComparisonDataset(report, metrics, primaryDataset, comparisonDataset, fiscalYearMonthName) : comparisonDataset;
  const dimensionCount = dimensions.count();
  const chartType = report.get('chartType');
  if (chartType === COMBINATION) {
    const summaryDataset = dataMap.getIn(['summary', 'data']);
    const primaryLayers = inferXYChartLayers('primary', dimensions, metrics.take(1));
    const combinationLayers = inferXYCombinationLayers(dimensionCount === 2 ? 'summary' : 'primary', dimensions, metrics.shift());
    return applyXYDisplayParams(XYVisualization({
      type: 'XY',
      data: {
        primary: primaryDataset,
        summary: summaryDataset
      },
      layers: primaryLayers.concat(combinationLayers),
      style: {
        type: MarkTypes.bar,
        dataLabels: true,
        isMobile,
        disableZoom
      }
    }), report, brandKitPalettes);
  }
  const primaryLayers = primaryDataset ? inferXYChartLayers('primary', dimensions, metrics) : List();

  // Two dimensional comparison charts, currently only used by social team
  const comparisonLayers = updatedComparisonDataset && dimensionCount < 2 ? inferXYComparisonLayers('compare', dimensions, metrics, chartType) : List();
  const insightsLayers = hasInsightDatasets ? insightDatasets.reduce((list, insightDataset, key) => {
    return /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */(
      list.concat(inferXYInsightLayers(report, key, insightDataset, dimensions))
    );
  }, List()) : List();
  const overrideYAxisTitle = !updatedComparisonDataset && dimensions.count() === 1 && metrics.count() > 1 ? {
    y: {
      left: {
        title: ''
      }
    }
  } : {};
  const completeDatasets = dataMap.filter((_, key) => key.includes(CUSTOM_SERIES_DATASET_PREFIX)).merge(Object.assign({
    primary: primaryDataset,
    compare: updatedComparisonDataset
  }, insightDatasets.toJS()));
  const isAccumulated = report.getIn(['config', 'processors'], List()).includes(ACCUMULATE);
  const showDelta = report.getIn(['displayParams', 'showDelta']);
  const yLayer = metrics.get(0);
  const initialDeltaValue = showDelta && updatedComparisonDataset ? getTotal(updatedComparisonDataset.get('data'), yLayer, isAccumulated) : undefined;
  const finalDeltaValue = showDelta ? getTotal(primaryDataset.get('data'), yLayer, isAccumulated) : undefined;
  const dateResolution = report.getIn(['config', 'frequency']);
  const allLayers = comparisonLayers.concat(primaryLayers).concat(insightsLayers);
  let visualization = applyXYDisplayParams(XYVisualization({
    type: 'XY',
    data: completeDatasets,
    layers: allLayers,
    style: Object.assign({
      isMobile,
      disableZoom,
      delta: showDelta ? {
        initial: initialDeltaValue,
        final: finalDeltaValue
      } : undefined,
      type: ((chartType === COLUMN ? MarkTypes.bar : chartType) || MarkTypes.bar).toLowerCase()
    }, overrideYAxisTitle, {
      dateResolution
    })
  }), report, brandKitPalettes);

  // bar charts are represented as inverted column charts (we invert the x/y axis)
  if (chartType === BAR) {
    visualization = transposeChart(visualization);
  }
  return visualization;
};
const inferFunnel = (report, dimensions, metrics, dataMap) => {
  const primaryDataset = dataMap.getIn(['primary', 'data']) || Dataset({});
  const chartType = report.get('chartType');
  if ((report.getIn(['config', 'processors']) || List()).includes(ZERO_DIMENSIONAL_METRIC_FUNNEL)) {
    return FunnelVisualization({
      type: FUNNEL,
      data: {
        primary: primaryDataset
      },
      dataset: 'primary',
      filterProperty: ZERO_DIMENSIONAL_METRIC_FUNNEL_DIMENSION,
      valueProperty: ZERO_DIMENSIONAL_METRIC_FUNNEL_METRIC,
      horizontalFunnel: chartType === COLUMN
    });
  }
  const [primaryChevronProperty, secondaryChevronProperty] = report.getIn(['config', 'metrics']).skip(1).map(metric => `${metric.get('metricTypes').first()}|${metric.get('property')}`).toArray();
  return FunnelVisualization({
    type: FUNNEL,
    data: {
      primary: primaryDataset
    },
    dataset: 'primary',
    filterProperty: dimensions.first(),
    valueProperty: metrics.first(),
    horizontalFunnel: chartType === COLUMN,
    primaryChevronProperty,
    secondaryChevronProperty
  });
};
export const inferVisualization = (report = ImmutableMap(), dataMap, isMobile = false, fiscalYearMonthName = DEFAULT_FISCAL_YEAR_MONTH_NAME, brandKitPalettes, compact, mobileReportingOptions, disableZoom) => {
  const configType = report.getIn(['config', 'configType']);
  const chartType = report.get('chartType');
  let dimensions = report.getIn(['config', 'dimensions'], List());
  const metrics = report.getIn(['config', 'metrics']).flatMap(metric => {
    if (report.getIn(['config', 'dataType']) === UNIFIED_EVENTS) {
      return List([metric.get('property')]);
    }
    const metricTypes = metric.get('metricTypes');
    return metricTypes.isEmpty() ? List([toMetricKey({
      property: metric.get('property'),
      SUM
    })]) : metricTypes.map(type => toMetricKey({
      property: metric.get('property'),
      type
    }));
  }) || List();
  if (chartType === TABLE) {
    if (configType === SEARCH) {
      const hideKeyColumn = report.getIn(['displayParams', 'hideKeyColumn'], false);

      // TODO: When migration to BE is done, this will not be in dataMap, it will be hs_object_id for all data types. Right now we are using the legacy id properties during migrations
      const searchDimension = dataMap.getIn(['primary', 'data', 'searchDimension']) || searchDimensionMap.get(report.getIn(['config', 'dataType'], 'hs_object_id'));
      dimensions = hideKeyColumn || !searchDimension ? List() : List([searchDimension]);
    }
    return inferTable(report, dimensions, metrics, dataMap, isMobile, mobileReportingOptions);
  }
  if (chartType === DATA_WELL) {
    return inferDataWell(report, dimensions, metrics, dataMap, compact);
  }
  if (chartType === PIE || chartType === DONUT) {
    return inferSliced(report, dimensions, metrics, dataMap, isMobile, brandKitPalettes);
  }
  if (chartType === FUNNEL || chartType === COLUMN && (configType === FUNNEL_CONFIG_TYPE || configType === PIPELINE)) {
    return inferFunnel(report, dimensions, metrics, dataMap);
  }
  return inferXY(report, dimensions, metrics, dataMap, isMobile, fiscalYearMonthName, brandKitPalettes, disableZoom);
};
export const __TESTABLE__ = {
  getOnTableCellLinkClickCallback
};